import type { LocalVideoTrack } from 'livekit-client';
import { apiBase } from '../lib/runtime';

/**
 * A kamerakep hattere: semmi, elmosas, vagy egy sajat feltoltott kep.
 *
 * **A modell es a WASM SAJAT kiszolgalonkrol jon.** A csomag alapbol a
 * jsDelivrrol es a Google tarolojabol toltene - egy szigoru ceges halozat
 * mogul viszont nem biztos, hogy atmegy, es akkor a kapcsolo NEMAN nem
 * csinalna semmit. A fajlokat ezert a `public/mediapipe` ala masoljuk
 * (lasd `scripts/copy-mediapipe.mjs`).
 */

export type VideoBackground =
  | { kind: 'none' }
  | { kind: 'blur' }
  /** Sajat hatter; az `url` relativ (lasd data/huddle-backgrounds.ts). */
  | { kind: 'image'; id: string; url: string };

export const NO_BACKGROUND: VideoBackground = { kind: 'none' };

export const sameBackground = (a: VideoBackground, b: VideoBackground): boolean =>
  a.kind === b.kind && (a.kind !== 'image' || (b.kind === 'image' && a.id === b.id));

/**
 * Az elmosas erossege.
 *
 * A 12 keves volt: a hatterben a targyak korvonala, egy tabla felirata meg
 * kivehetoen latszott. 30-nal a hatter egybefolyik.
 */
const BLUR_RADIUS = 30;

/**
 * A megjegyzett beallitas.
 *
 * Aki egyszer beallitotta, az legkozelebb is azt akarja - nem azert valasztott
 * hatteret, mert aznap volt rendetlen a szoba. Gepenkent ervenyes.
 */
const STORAGE_KEY = 'avslack.huddle.background';
/** A regi, csak elmosast ismero beallitas ('be' / 'ki'). */
const LEGACY_BLUR_KEY = 'avslack.huddle.blur';

export function backgroundPreference(): VideoBackground {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as VideoBackground;
      if (parsed.kind === 'blur' || parsed.kind === 'none') return parsed;
      if (parsed.kind === 'image' && parsed.id && parsed.url) return parsed;
    }
    return localStorage.getItem(LEGACY_BLUR_KEY) === 'be' ? { kind: 'blur' } : NO_BACKGROUND;
  } catch {
    // Privat ablakban vagy tiltott tarolasnal: alapertelmezes szerint semmi.
    return NO_BACKGROUND;
  }
}

export function rememberBackground(background: VideoBackground): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(background));
  } catch {
    // A tarolas hianya nem torheti el a hivast.
  }
}

const ASSET_PATHS = {
  tasksVisionFileSet: '/mediapipe/wasm',
  modelAssetPath: '/mediapipe/selfie_segmenter.tflite',
};

/**
 * Egy valodi feladat-hatar (nem mikrofeladat).
 *
 * A `MessageChannel` nem esik a `setTimeout` 4 ms-os also korlatja ala, megis
 * visszaadja a vezerlest az esemenyhuroknak: kozben lefut a kattintas, a
 * billentyu, a rajzolas.
 */
const yieldToEventLoop = (): Promise<void> =>
  new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
      channel.port1.close();
      resolve();
    };
    channel.port2.postMessage(null);
  });

/*
  AZ ORSZEM hatarai.

  Az elso nehany kocka nem szamit: abban a GPU-shaderek forditasa van, az
  minden gepen lassu. Utana ha a kockankenti munka atlaga tartosan a kamera
  ~33 ms-os utemezese fole megy, a gep nem birja - a hatter kikapcsol, mielott
  a felulet hasznalhatatlan lenne. Egyetlen, masodpercnel hosszabb kocka pedig
  onmagaban is eleg ok.
*/
const WARMUP_FRAMES = 10;
const WINDOW_FRAMES = 20;
const MAX_AVG_FRAME_MS = 45;
const MAX_SINGLE_FRAME_MS = 1000;

/**
 * A hatter-szuro tul lassu ezen a gepen - a szuro mar le van allitva.
 * A hivo csak az allapotot es az uzenetet frissiti.
 */
export type OnBackgroundTooSlow = () => void;

/**
 * A hatter-valtasok SORBAN futnak, savonkent.
 *
 * A kamera bekapcsolasa is alkalmazza a megjegyzett hatteret, es kozben a
 * valaszto is kattinthato. Sorrend nelkul ket `setProcessor` futhatott
 * egyszerre ugyanazon a savon - ket feldolgozo, ketszeres fo-szal terheles.
 */
const queues = new WeakMap<LocalVideoTrack, Promise<unknown>>();

/**
 * A hatter beallitasa egy elo kamerasavon. Azt adja vissza, ami TENYLEG ervenybe
 * lepett.
 *
 * SOSEM dob: ha a modell vagy a kep nem tolthet be (regi gep, hianyzo
 * WASM-tamogatas, torolt kep), a kamera akkor is menjen tovabb - hatter nelkul.
 * Egy nem mukodo szurotol nem eshet szet a hivas.
 *
 * Ha mar fut a processzor, csak ATKAPCSOLJUK (`switchTo`): egy uj processzor
 * felepitese egy pillanatra a nyers kepet mutatna - eppen azt, amit a hatter
 * el akar takarni.
 */
export function applyBackground(
  track: LocalVideoTrack,
  background: VideoBackground,
  onTooSlow?: OnBackgroundTooSlow,
): Promise<VideoBackground> {
  const previous = queues.get(track) ?? Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(() => applyBackgroundNow(track, background, onTooSlow));
  queues.set(track, next);
  return next;
}

async function applyBackgroundNow(
  track: LocalVideoTrack,
  background: VideoBackground,
  onTooSlow?: OnBackgroundTooSlow,
): Promise<VideoBackground> {
  try {
    if (background.kind === 'none') {
      await track.stopProcessor();
      return NO_BACKGROUND;
    }

    const options =
      background.kind === 'blur'
        ? ({ mode: 'background-blur', blurRadius: BLUR_RADIUS } as const)
        : ({ mode: 'virtual-background', imagePath: apiBase() + background.url } as const);

    // A feldolgozo (MediaPipe) nagy: csak az elso hatter-valasztaskor toltjuk be (K2).
    const { BackgroundProcessor, BackgroundProcessorWrapper } =
      await import('@livekit/track-processors');
    const current = track.getProcessor();
    if (current instanceof BackgroundProcessorWrapper && current.mode !== 'legacy') {
      await current.switchTo(options);
    } else {
      const processor = BackgroundProcessor({
        ...options,
        assetPaths: ASSET_PATHS,
        onFrameProcessed: watchdog(track, onTooSlow),
      });
      yieldAfterEachFrame(processor.transformer);
      await track.setProcessor(processor);
    }
    return background;
  } catch {
    await track.stopProcessor().catch(() => undefined);
    return NO_BACKGROUND;
  }
}

/**
 * MINDEN KOCKA UTAN visszaadjuk a vezerlest az esemenyhuroknak.
 *
 * ─── Miert kell? ────────────────────────────────────────────────────────────
 *
 * A szegmentalas es a rajzolas szinkron, a FO SZALON fut (a csomag maga is
 * irja: kockankent tiz-szaz ms). Ha egy gepen ez lassabb, mint ahogy a kamera
 * a kockakat adja, mindig var a kovetkezo - es a stream azt mikrofeladatkent
 * veszi fel, vagyis a bongeszo SOSEM jut el a kattintasokig. Elesben ez igy
 * nezett ki: az elmosas bekapcsolasa utan befagyott az also gombsor, majd a
 * Chrome "Tobb oldal nem valaszol" ablakot dobott (az ölelés-ablak es a fo
 * ablak egy folyamatban fut, egyutt alltak meg).
 *
 * A szunet utan a kovetkezo kocka erkezik - a lassu gepen ettol kevesebb
 * kocka megy at, de a felulet valaszol.
 */
function yieldAfterEachFrame(transformer: {
  transform: (
    frame: VideoFrame,
    controller: TransformStreamDefaultController<VideoFrame>,
  ) => unknown;
}): void {
  const original = transformer.transform.bind(transformer);
  transformer.transform = async (frame, controller) => {
    await original(frame, controller);
    await yieldToEventLoop();
  };
}

/**
 * ORSZEM: ha a gep nem birja a hatteret, kikapcsoljuk - a kamera megy tovabb.
 *
 * Inkabb latszodjon a szoba, mint hogy az egesz hivas hasznalhatatlan legyen.
 */
function watchdog(
  track: LocalVideoTrack,
  onTooSlow?: OnBackgroundTooSlow,
): (stats: { processingTimeMs: number }) => void {
  let frames = 0;
  let tripped = false;
  const recent: number[] = [];

  return ({ processingTimeMs }) => {
    if (tripped) return;
    frames += 1;
    if (frames <= WARMUP_FRAMES) return;

    recent.push(processingTimeMs);
    if (recent.length > WINDOW_FRAMES) recent.shift();
    const avg = recent.reduce((sum, ms) => sum + ms, 0) / recent.length;

    const tooSlow =
      processingTimeMs > MAX_SINGLE_FRAME_MS ||
      (recent.length === WINDOW_FRAMES && avg > MAX_AVG_FRAME_MS);
    if (!tooSlow) return;

    tripped = true;
    rememberBackground(NO_BACKGROUND);
    void applyBackground(track, NO_BACKGROUND).then(() => onTooSlow?.());
  };
}
